/**
 * NHAI GIS layer catalog — single source of truth for TOC folders,
 * create-asset pickers, and overlay visibility filters.
 */
import { inferLrsGrade, type LrsGrade } from './lrs-asset-types';

export type LrsCatalogSource =
  | 'event'
  | 'utility'
  | 'carriageway'
  | 'lane'
  | 'pavement_condition'
  | 'chainage'
  | 'basemap'
  | 'stub';

export interface LrsCatalogLeaf {
  id: string;
  label: string;
  source: LrsCatalogSource;
  typeIds?: string[];
  grade?: LrsGrade;
  basemapKey?: string;
  stubHint?: string;
}

export interface LrsCatalogGroup {
  id: string;
  label: string;
  children: LrsCatalogLeaf[];
}

export const LRS_LAYER_CATALOG: readonly LrsCatalogGroup[] = [
  {
    id: 'basemaps',
    label: 'Base Maps',
    children: [
      { id: 'bm-satellite', label: 'Satellite', source: 'basemap', basemapKey: 'googleSatellite' },
      { id: 'bm-hybrid', label: 'Hybrid', source: 'basemap', basemapKey: 'googleHybrid' },
      { id: 'bm-terrain', label: 'Terrain', source: 'basemap', basemapKey: 'googleTerrain' },
      { id: 'bm-osm', label: 'OSM', source: 'basemap', basemapKey: 'osm' },
    ],
  },
  {
    id: 'nhai-road',
    label: 'NHAI Road Assets',
    children: [
      { id: 'road-pavement', label: 'Pavement', source: 'event', typeIds: ['pavement'] },
      { id: 'road-shoulder', label: 'Shoulder', source: 'event', typeIds: ['shoulder'] },
      { id: 'road-median', label: 'Median', source: 'event', typeIds: ['median'] },
      { id: 'road-camber', label: 'Camber', source: 'event', typeIds: ['camber'] },
      { id: 'road-drainage', label: 'Drainage', source: 'utility', typeIds: ['drainage'] },
      { id: 'road-corridor', label: 'Corridor', source: 'carriageway' },
      {
        id: 'road-furniture',
        label: 'Road furniture',
        source: 'event',
        typeIds: [
          'sign',
          'sign_board',
          'km_stone',
          'chainage_marker',
          'guard_rail',
          'crash_barrier',
          'delineator',
          'road_stud',
          'lighting',
          'gantry',
          'bus_shelter',
          'bollard',
          'cctv',
          'vms',
          'atms',
          'sos',
          'wim',
          'toll_plaza',
          'bus_bay',
          'truck_bay',
          'breakdown_parking',
        ],
      },
      {
        id: 'road-structures',
        label: 'Road structures',
        source: 'event',
        typeIds: [
          'bridge',
          'rob',
          'rub',
          'flyover',
          'viaduct',
          'box_culvert',
          'retaining_wall',
          'subway',
          'underpass',
          'overpass',
        ],
      },
    ],
  },
  {
    id: 'ug-utilities',
    label: 'Underground Utilities',
    children: [
      { id: 'ug-water', label: 'Water pipeline', source: 'utility', typeIds: ['water'], grade: 'underground' },
      { id: 'ug-sewer', label: 'Sewer', source: 'utility', typeIds: ['sewer'], grade: 'underground' },
      { id: 'ug-storm', label: 'Storm-water drainage', source: 'utility', typeIds: ['storm_drain'], grade: 'underground' },
      { id: 'ug-oil-gas', label: 'Oil & gas', source: 'utility', typeIds: ['oil_gas'], grade: 'underground' },
      {
        id: 'ug-electric',
        label: 'Electricity',
        source: 'utility',
        typeIds: ['electric_ht', 'electric_lt'],
        grade: 'underground',
      },
      { id: 'ug-ofc', label: 'Telecom/fiber', source: 'utility', typeIds: ['ofc', 'utility_duct'], grade: 'underground' },
      {
        id: 'ug-chambers',
        label: 'Chambers / manholes',
        source: 'utility',
        typeIds: ['chamber', 'manhole', 'valve_chamber', 'inspection_chamber'],
        grade: 'underground',
      },
    ],
  },
  {
    id: 'og-utilities',
    label: 'Overground Utilities',
    children: [
      {
        id: 'og-electric-lines',
        label: 'Electricity lines',
        source: 'utility',
        typeIds: ['electric_ht', 'electric_lt'],
        grade: 'overground',
      },
      { id: 'og-ofc', label: 'Telecom/fiber', source: 'utility', typeIds: ['ofc'], grade: 'overground' },
      { id: 'og-tank', label: 'O/H water tank', source: 'utility', typeIds: ['oh_water_tank'] },
      {
        id: 'og-towers',
        label: 'Electricity towers',
        source: 'utility',
        typeIds: ['electric_tower', 'electric_pole', 'telecom_tower'],
      },
      {
        id: 'og-other',
        label: 'Other utility assets',
        source: 'utility',
        typeIds: ['oh_water_tank', 'electric_pole', 'telecom_tower'],
        grade: 'overground',
      },
    ],
  },
  {
    id: 'crossings',
    label: 'Crossing Assets',
    children: [
      { id: 'xo-culvert', label: 'Culverts', source: 'event', typeIds: ['culvert', 'box_culvert'] },
      { id: 'xo-bridge', label: 'Bridges', source: 'event', typeIds: ['bridge'] },
      { id: 'xo-fiber', label: 'Fiber crossings', source: 'utility', typeIds: ['ofc'], grade: 'crossover' },
      {
        id: 'xo-pipe',
        label: 'Pipe crossings',
        source: 'utility',
        typeIds: ['water', 'sewer', 'storm_drain', 'oil_gas', 'drainage'],
        grade: 'crossover',
      },
      {
        id: 'xo-electric',
        label: 'Electrical crossings',
        source: 'utility',
        typeIds: ['electric_ht', 'electric_lt'],
        grade: 'crossover',
      },
    ],
  },
  {
    id: 'reference',
    label: 'Reference layers',
    children: [
      {
        id: 'ref-village',
        label: 'Village boundaries',
        source: 'stub',
        stubHint: 'No village boundary data yet',
      },
      {
        id: 'ref-survey',
        label: 'Survey boundaries',
        source: 'stub',
        stubHint: 'No survey boundary data yet',
      },
      { id: 'ref-chainage', label: 'Chainage', source: 'chainage' },
    ],
  },
];

export function catalogGroups(includeBasemaps = false): LrsCatalogGroup[] {
  return LRS_LAYER_CATALOG.filter((g) => includeBasemaps || g.id !== 'basemaps').map((g) => ({
    ...g,
    children: [...g.children],
  }));
}

export function flattenCatalogLeaves(includeBasemaps = false): LrsCatalogLeaf[] {
  return catalogGroups(includeBasemaps).flatMap((g) => g.children);
}

export function defaultLayerVisibility(includeBasemaps = false): Record<string, boolean> {
  const vis: Record<string, boolean> = {};
  for (const leaf of flattenCatalogLeaves(includeBasemaps)) {
    vis[leaf.id] = leaf.source !== 'stub';
  }
  return vis;
}

export interface CatalogFeatureProps {
  event_type?: string | null;
  utility_type?: string | null;
  grade?: unknown;
  depth_m?: number | null;
  lrsKind?: string | null;
  properties?: Record<string, unknown> | null;
}

export function featureMatchesLeaf(props: CatalogFeatureProps, leaf: LrsCatalogLeaf): boolean {
  if (leaf.source === 'stub' || leaf.source === 'basemap') return false;
  if (leaf.source === 'chainage') {
    return props.event_type === 'chainage' || props.lrsKind === 'chainage';
  }
  if (leaf.source === 'carriageway' || leaf.source === 'lane') {
    return props.lrsKind === 'carriageways' || props.lrsKind === 'lanes' || props.lrsKind === 'corridor';
  }
  if (leaf.source === 'pavement_condition') {
    return props.lrsKind === 'pavement_condition' || props.event_type === 'pavement';
  }
  const typeId = String(props.utility_type || props.event_type || '');
  if (leaf.typeIds?.length && !leaf.typeIds.includes(typeId)) return false;
  if (leaf.grade) {
    const grade = inferLrsGrade({
      grade: props.grade,
      depth_m: props.depth_m,
      event_type: props.event_type,
      properties: props.properties,
    });
    if (grade !== leaf.grade) return false;
  }
  if (leaf.source === 'utility') return !!props.utility_type;
  if (leaf.source === 'event') return !!props.event_type && props.event_type !== 'chainage';
  return true;
}

export function isCatalogFeatureVisible(
  props: CatalogFeatureProps,
  visibility: Record<string, boolean>
): boolean {
  const leaves = flattenCatalogLeaves(false);
  const matching = leaves.filter((leaf) => featureMatchesLeaf(props, leaf));
  if (!matching.length) return true;
  return matching.some((leaf) => visibility[leaf.id] !== false);
}

export function toggleCatalogNode(
  nodeId: string,
  checked: boolean,
  visibility: Record<string, boolean>
): Record<string, boolean> {
  const next = { ...visibility };
  const group = LRS_LAYER_CATALOG.find((g) => g.id === nodeId);
  if (group) {
    for (const child of group.children) {
      if (child.source === 'stub') continue;
      next[child.id] = checked;
    }
    return next;
  }
  next[nodeId] = checked;
  return next;
}

export function groupCheckState(
  group: LrsCatalogGroup,
  visibility: Record<string, boolean>
): 'all' | 'some' | 'none' {
  const leaves = group.children.filter((c) => c.source !== 'stub' && c.source !== 'basemap');
  if (!leaves.length) return 'none';
  const on = leaves.filter((c) => visibility[c.id] !== false).length;
  if (on === 0) return 'none';
  if (on === leaves.length) return 'all';
  return 'some';
}

export function countFeaturesForLeaf(
  leaf: LrsCatalogLeaf,
  features: CatalogFeatureProps[]
): number {
  if (leaf.source === 'stub' || leaf.source === 'basemap') return 0;
  return features.filter((f) => featureMatchesLeaf(f, leaf)).length;
}
